import { useState } from "react";
import { ShieldIcon, ShieldPlusIcon, Trash2Icon } from "lucide-react";

import {
  PermissionEnum,
  type EntityFragment,
  type RoleFragment,
} from "@/__generated__/graphql";
import { SettingsViewCard } from "@/app/app/_/views/SettingsView/SettingsViewLayout";
import { Icon } from "@/components/atoms/Icon";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { ROLE_PERMISSIONS } from "@/components/organisms/Roles/Roles.content";
import { describePermissions } from "@/components/organisms/Roles/Roles.tools";
import { useEntityRoles } from "@/components/organisms/Roles/Roles.hooks";
import { Mutations } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useScope } from "@/hooks/useScope";
import { State } from "@/state";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { Input } from "@/components/ui/input";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

/** The role drawn while the roles load: a typical one, each piece wrapped. */
const PLACEHOLDER_ROLE: RoleFragment = {
  __typename: "RoleObject",
  id: "placeholder",
  identifier: "placeholder",
  createdAt: "",
  updatedAt: "",
  roleName: "Bookkeeper",
  isDefault: true,
  isEditable: false,
  permissions: [
    PermissionEnum.Book,
    PermissionEnum.Reconcile,
    PermissionEnum.Report,
  ],
};

/**
 * A business's roles: what each allows its holders besides reading the books
 * - the four every business has (Administrator, Accountant, Bookkeeper,
 * Viewer), fixed, and any its owners add, renamed, changed and deleted here by
 * them. Every member who is no owner holds one; books shared with a firm are
 * worked in as what both the business's role on the grant and the firm's on
 * its hand-on to each of its people allow.
 */
export function EntityRolesPanel({ entity }: { entity: EntityFragment }) {
  const { accounts } = useScope();
  const isOwner = Boolean(
    accounts?.find(
      (account) => account?.entity?.identifier === entity.identifier,
    )?.isOwner,
  );
  const { roles, isLoading } = useEntityRoles(entity.id);
  const [name, setName] = useState("");
  const [permissions, setPermissions] = useState<PermissionEnum[]>([]);

  const { mutate: createRole, loading: isCreating } = useBackendMutation({
    mutation: Mutations.CREATE_ROLE,
    refetchQueries: ["EntityRoles"],
  });
  const { mutate: updateRole } = useBackendMutation({
    mutation: Mutations.UPDATE_ROLE,
    refetchQueries: ["EntityRoles"],
  });
  const { mutate: deleteRole, loading: isDeleting } = useBackendMutation({
    mutation: Mutations.DELETE_ROLE,
    refetchQueries: ["EntityRoles"],
  });

  const toggle = (list: PermissionEnum[], permission: PermissionEnum) =>
    list.includes(permission)
      ? list.filter((one) => one !== permission)
      : [...list, permission];

  return (
    <div className="flex flex-col gap-3 p-4">
      {isOwner && (
        <SettingsViewCard
          icon={ShieldPlusIcon}
          title="New role"
          description="Name it, then tick what it allows besides viewing the books."
        >
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void createRole({
                variables: {
                  input: {
                    entityIdentifier: entity.identifier,
                    name: name.trim(),
                    permissions,
                  },
                },
                callbacks: {
                  onSuccess: () => {
                    setName("");
                    setPermissions([]);
                    State.getState().setSnackbarMessage("Role created.");
                  },
                },
              });
            }}
            className="flex items-center gap-x-3 gap-y-2 pl-3 pr-3 min-h-11 [border-top-width:1px] [border-top-style:solid] border-border pt-2 pb-2 flex-wrap"
          >
            <div className="flex text-muted-foreground/80">
              <Icon ReactIcon={ShieldIcon} size="md" />
            </div>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Bookkeeper"
              aria-label="Role name"
              data-testid="entity-roles-new-name"
              className="h-8 w-auto"
            />
            {ROLE_PERMISSIONS.map(({ permission, label, description }) => (
              <Label
                key={permission}
                data-testid={`entity-roles-new-${permission}`}
                title={description}
                className="flex items-center gap-2"
              >
                <Checkbox
                  checked={permissions.includes(permission)}
                  onCheckedChange={() =>
                    setPermissions((was) => toggle(was, permission))
                  }
                />
                {label}
              </Label>
            ))}
            <Button
              variant="ghost"
              size="sm"
              type="submit"
              disabled={isCreating || !name.trim()}
              data-testid="entity-roles-create"
            >
              {isCreating && <Spinner />}Create role
            </Button>
          </form>
        </SettingsViewCard>
      )}

      <SettingsViewCard
        icon={ShieldIcon}
        title="Roles"
        description={
          isOwner
            ? "Each allows viewing the books, and what is ticked. Every member who is not an owner holds one; a firm the books are shared with works in them as what its role here allows."
            : `Only owners of ${entity.name ?? "this business"} manage its roles.`
        }
        isLoading={isLoading}
        data-testid="entity-roles-list"
      >
        {(isLoading ? [PLACEHOLDER_ROLE] : roles).map((role) => (
          <EntityRolesRow
            key={role.id}
            role={role}
            isLoading={isLoading}
            isOwner={isOwner}
            isDeleting={isDeleting}
            onRename={(next) =>
              void updateRole({
                variables: {
                  input: { roleIdentifier: role.identifier, name: next },
                },
              })
            }
            onToggle={(permission) =>
              void updateRole({
                variables: {
                  input: {
                    roleIdentifier: role.identifier,
                    permissions: toggle(role.permissions, permission),
                  },
                },
              })
            }
            onDelete={() => {
              if (!window.confirm(`Delete ${role.roleName}?`)) {
                return;
              }
              void deleteRole({
                variables: { input: { roleIdentifier: role.identifier } },
              });
            }}
          />
        ))}
      </SettingsViewCard>
    </div>
  );
}

/**
 * One role: its name - typed over, for one its business's owners added - what
 * it allows, ticked, and, while hovered, what that means in plain words.
 */
function EntityRolesRow({
  role,
  isLoading,
  isOwner,
  isDeleting,
  onRename,
  onToggle,
  onDelete,
}: {
  role: RoleFragment;
  isLoading: boolean;
  isOwner: boolean;
  isDeleting: boolean;
  onRename: (name: string) => void;
  onToggle: (permission: PermissionEnum) => void;
  onDelete: () => void;
}) {
  const isChangeable = isOwner && role.isEditable;
  const [name, setName] = useState(role.roleName);
  const hint = role.isEditable
    ? isOwner
      ? "Untick what it should not allow; its holders can always view."
      : null
    : "Every business has this role: what it allows stays as it is.";

  return (
    <div
      data-testid={`entity-role-${role.identifier}`}
      className="flex flex-col gap-1 pl-3 pr-3 pt-2 pb-2 [border-top-width:1px] [border-top-style:solid] border-border [&:hover_[data-hint]]:opacity-[1] [&:focus-within_[data-hint]]:opacity-[1]"
    >
      <div className="flex items-center gap-3 min-h-7">
        <Skeleton
          loading={isLoading}
          variant="rectangular"
          className="w-fit max-w-full flex [flex-direction:inherit] [flex-wrap:inherit] [align-items:inherit] [justify-content:inherit] gap-[inherit]"
        >
          <div className="flex text-muted-foreground/80">
            <Icon ReactIcon={ShieldIcon} size="md" />
          </div>
        </Skeleton>
        {isChangeable ? (
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              if (name.trim() && name.trim() !== role.roleName) {
                onRename(name.trim());
              }
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                (event.target as HTMLInputElement).blur();
              }
            }}
            aria-label={`Rename ${role.roleName}`}
            className="border-0 bg-transparent shadow-none dark:bg-transparent focus-visible:ring-0 h-8"
          />
        ) : (
          <Typography level="body-md" className="truncate shrink-0 max-w-[35%]">
            <Skeleton loading={isLoading} variant="inline">
              {role.roleName}
            </Skeleton>
          </Typography>
        )}
        {role.isDefault && !isLoading && (
          <SimpleEnum label="Default" enumColor={EnumColors.GREY_1} />
        )}
        <Typography
          level="body-sm"
          data-testid={`entity-role-${role.identifier}-allows`}
          className="truncate flex-1 min-w-0 text-muted-foreground"
        >
          <Skeleton loading={isLoading} variant="inline">
            {describePermissions(role.permissions)}
          </Skeleton>
        </Typography>
        <div className="w-8 flex justify-end">
          {isChangeable && (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Delete ${role.roleName}`}
              onClick={onDelete}
              disabled={isDeleting}
              className="text-destructive hover:text-destructive"
            >
              {isDeleting ? <Spinner /> : <Trash2Icon size="1em" />}
            </Button>
          )}
        </div>
      </div>
      {isChangeable && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 pl-8">
          {ROLE_PERMISSIONS.map(({ permission, label, description }) => (
            <Label
              key={permission}
              data-testid={`entity-role-${role.identifier}-${permission}`}
              title={description}
              className="flex items-center gap-2"
            >
              <Checkbox
                checked={role.permissions.includes(permission)}
                onCheckedChange={() => onToggle(permission)}
              />
              {label}
            </Label>
          ))}
        </div>
      )}
      <Typography
        level="body-xs"
        data-hint
        className="truncate pl-8 text-muted-foreground/80 opacity-[0]"
      >
        {isLoading ? null : hint}
      </Typography>
    </div>
  );
}
